import { Form, Formik } from 'formik';
import ModalComponent from '../../../components/modal/modal';
import { useEffect, useRef, useState } from 'react';
import ModalBody from '../../../components/modal/modalBodyComponent';
import { PermissionConfirmScrollWrapper } from './manageFeatureSetModal.styled';
import Tag from '../../../components/lozenge/tags';
import Heading from '../../../components/typography';
import {
  ROLES_PERMISSION_LIST,
  SORTED_FIELD_PERMISSION,
  SORTED_MODULE_PERMISSION,
} from '../../../constants/common';
import { handleFilterPermissionList, handleSortPermissionNames } from '../../../utils/global';
import { getFilteredPermissionData } from '../../../utils';
import {
  convertDualPermissions,
  convertPermissions,
} from '../../../utils/permission/permissionAdapter';
import {
  useAdvanceBoolean,
  useGetFeaturesPermissionsDistributable,
  usePostUpsertPermission,
} from '../../../hooks';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import AlertPopup from '../../../components/alert-popup';
import useGetFeaturePermissionSet from '../../../hooks/get/useGetFeaturePermissionSet';
import {
  PermissionMatrixForm,
  useFilteredPermissionMatrix,
  getBulkEditTitle,
  getBulkEditUrlName,
  BULK_MODAL_TOOLTIP,
} from '../shared/permission-matrix';

interface ManageFeatureSetModalTypes {
  isModalOpen: boolean;
  closeModal: () => void;
  id?: string;
  checkedValues: string[];
  setCheckedValues: (values: string[]) => void;
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  permissionType?: 'roles' | 'feature-set';
  selectedRowData: any;
}

const ManageFeatureSetModal = ({
  isModalOpen,
  closeModal,
  id,
  checkedValues,
  setCheckedValues,
  handleRequest,
  permissionType = 'feature-set',
  selectedRowData,
}: ManageFeatureSetModalTypes): JSX.Element => {
  const userAuthList = useSelector(selectAuthRoutesList);
  const allFieldValues = userAuthList.localization?.values?.Wealthlane;
  const [manualSearch, setManualSearch] = useState('');
  const successModalDataRef = useRef<{
    payload: any;
    selectedPermissionNames: string[];
    selectedPermissionRows: {
      displayName: string;
      permissions: { text: string; allowed: boolean; applicable?: boolean }[];
    }[];
  } | null>(null);
  const {
    value: isSuccessModalOpen,
    setFalse: closeSuccessModal,
    setTrue: openSuccessModal,
    customValue: successCustomValue,
    setCustomValue: setSuccessCustomValue,
  } = useAdvanceBoolean(false);

  const { mutate: upsertPermission, isLoading } = usePostUpsertPermission(
    handleRequest,
    closeModal,
    setCheckedValues
  );
  const { data: newPermissions } = useGetFeaturePermissionSet(handleRequest);
  const { data: distributablePermissions } = useGetFeaturesPermissionsDistributable(handleRequest);

  const [moduleLevelPermissions, setModuleLevelPermissions] = useState<any[]>([]);
  const [fieldLevelPermissionsList, setFieldLevelPermissionsList] = useState<any[]>([]);

  useEffect(() => {
    if (distributablePermissions?.fieldLevelPermissions) {
      setModuleLevelPermissions(distributablePermissions?.permissions ?? []);
      setFieldLevelPermissionsList(distributablePermissions?.fieldLevelPermissions ?? []);
    }
  }, [distributablePermissions]);

  const { nonFieldLevelPermissionsGroups, fieldLevelPermissionsGroups } =
    permissionType === 'feature-set'
      ? (() => {
        const moduleGroups = getFilteredPermissionData(
          convertDualPermissions(moduleLevelPermissions),
          'CustomerService.Customer.',
          allFieldValues
        );
        const fieldGroups = getFilteredPermissionData(
          convertDualPermissions(fieldLevelPermissionsList),
          'CustomerService.Customer.',
          allFieldValues
        );
        return {
          nonFieldLevelPermissionsGroups: moduleGroups.nonFieldLevelPermissionsGroups,
          fieldLevelPermissionsGroups: fieldGroups.fieldLevelPermissionsGroups,
        };
      })()
      : getFilteredPermissionData(
        convertPermissions(newPermissions ?? []),
        'CustomerService.Customer.',
        allFieldValues
      );

  const filterNonFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(nonFieldLevelPermissionsGroups),
    SORTED_MODULE_PERMISSION
  );
  const filterFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(fieldLevelPermissionsGroups),
    SORTED_FIELD_PERMISSION
  );

  const { filteredData, filteredFieldLevelData } = useFilteredPermissionMatrix({
    filterNonFieldLevelPermission,
    filterFieldLevelPermission,
    manualSearch,
    allFieldValues,
  });

  const handleCheckedAll = (
    key: string,
    setFieldValue: any,
    dataList: any,
    wrapperName: string,
    isCheckedAll: boolean
  ): void => {
    const allFields = handleFilterPermissionList(dataList)[key];
    const filterAllDisabledData = allFields.filter(
      (item: any) => !(item.isDisabled as boolean) && item
    );

    if (isCheckedAll) {
      allFields.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, false)
      );
    } else {
      filterAllDisabledData.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, true)
      );
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        name: '',
        description: '',
        permissions: {},
        fieldLevelPermissions: {},
        selectedModulePermissions: {},
        selectedFieldPermissions: {},
      }}
      onSubmit={(values) => {
        const allowedPermissions: string[] = [];
        const deniedPermissions: string[] = [];

        Object.keys(values.selectedModulePermissions).forEach((rowKey) => {
          if (values.selectedModulePermissions[rowKey]) {
            const rowPermissions = handleFilterPermissionList(nonFieldLevelPermissionsGroups)[
              rowKey
            ];
            if (rowPermissions) {
              rowPermissions.forEach((item: any) => {
                if (!item.isDisabled && item.key) {
                  const permKey = item.key.replaceAll('.', '-');
                  if (values.permissions[permKey]) {
                    allowedPermissions.push(item.key);
                  } else {
                    deniedPermissions.push(item.key);
                  }
                }
              });
            }
          }
        });

        Object.keys(values.selectedFieldPermissions).forEach((rowKey) => {
          if (values.selectedFieldPermissions[rowKey]) {
            const rowPermissions = handleFilterPermissionList(fieldLevelPermissionsGroups)[rowKey];
            if (rowPermissions) {
              rowPermissions.forEach((item: any) => {
                if (!item.isDisabled && item.key) {
                  const permKey = item.key.replaceAll('.', '-');
                  if (values.fieldLevelPermissions[permKey]) {
                    allowedPermissions.push(item.key);
                  } else {
                    deniedPermissions.push(item.key);
                  }
                }
              });
            }
          }
        });

        const payload = {
          featureSetIds: permissionType === 'feature-set' ? checkedValues : [],
          roleIds: permissionType === 'roles' ? checkedValues : [],
          allowed: allowedPermissions,
          denied: deniedPermissions,
        };

        const selectedFieldPermissionNames = Object.keys(values.selectedFieldPermissions || {})
          .filter((rowKey) => values.selectedFieldPermissions[rowKey])
          .map((rowKey) => {
            const rowPermissions = handleFilterPermissionList(fieldLevelPermissionsGroups)[rowKey];
            const labelKey = rowPermissions?.[0]?.key;
            return (labelKey && allFieldValues?.[labelKey]) || rowKey;
          });

        const selectedModulePermissionNames = Object.keys(values.selectedModulePermissions || {})
          .filter((rowKey) => values.selectedModulePermissions[rowKey])
          .map((rowKey) => {
            const rowPermissions = handleFilterPermissionList(nonFieldLevelPermissionsGroups)[
              rowKey
            ];
            const labelKey = rowPermissions?.[0]?.key;
            return (labelKey && allFieldValues?.[labelKey]) || rowKey;
          });

        const selectedPermissionNames = [
          ...selectedModulePermissionNames,
          ...selectedFieldPermissionNames,
        ];

        const buildPermissionRows = (rowKeys: string[], groups: any) => {
          return rowKeys.map((rowKey) => {
            const rowPermissions = handleFilterPermissionList(groups)[rowKey];
            const labelKey = rowPermissions?.[0]?.key;
            const displayName = (labelKey && allFieldValues?.[labelKey]) || rowKey;
            const permissions = (ROLES_PERMISSION_LIST as string[]).map((permText) => {
              const item = rowPermissions?.find((p: any) => p.text === permText);
              const applicable = !!(item?.key && !item.isDisabled);
              const allowed = item?.key ? allowedPermissions.includes(item.key) : false;
              return { text: permText, allowed, applicable };
            });
            return { displayName, permissions };
          });
        };

        const moduleRows = buildPermissionRows(
          Object.keys(values.selectedModulePermissions || {}).filter(
            (k) => values.selectedModulePermissions[k]
          ),
          nonFieldLevelPermissionsGroups
        );
        const fieldRows = buildPermissionRows(
          Object.keys(values.selectedFieldPermissions || {}).filter(
            (k) => values.selectedFieldPermissions[k]
          ),
          fieldLevelPermissionsGroups
        );
        const selectedPermissionRows = [...moduleRows, ...fieldRows];

        const modalData = {
          payload,
          selectedPermissionNames,
          selectedPermissionRows,
        };
        successModalDataRef.current = modalData;
        setSuccessCustomValue(modalData);
        openSuccessModal();
      }}>
      {({ touched, errors, values, setFieldValue }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            size='xl'
            title={getBulkEditTitle(permissionType)}
            id={id}
            showToolTip={true}
            tooltipText={BULK_MODAL_TOOLTIP}
            children={
              <Form id='manageFeatureSetForm'>
                <ModalBody id='manageFeatureSetModalBody'>
                  {selectedRowData && (
                    <div className='flex flex-col gap-2 flex-start mb-8'>
                      <Heading
                        variant='h6'
                        title={
                          permissionType === 'feature-set'
                            ? 'Selected Feature Set'
                            : 'Selected Role'
                        }
                      />
                      <div className='flex flex-wrap gap-2 mt-4'>
                        {selectedRowData.map((item: any, index: number) => (
                          <Tag
                            key={index}
                            variant='primary'
                            title={permissionType === 'feature-set' ? item.name : item.roleName}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <PermissionMatrixForm
                    variant='bulk'
                    modalStatus='Edit'
                    values={values}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    allFieldValues={allFieldValues}
                    filteredData={filteredData}
                    filteredFieldLevelData={filteredFieldLevelData}
                    nonFieldLevelPermissionsGroups={nonFieldLevelPermissionsGroups}
                    fieldLevelPermissionsGroups={fieldLevelPermissionsGroups}
                    filterNonFieldLevelPermission={filterNonFieldLevelPermission}
                    filterFieldLevelPermission={filterFieldLevelPermission}
                    manualSearch={manualSearch}
                    onSearchChange={setManualSearch}
                    searchInputId='searchBulkEditInput'
                    handleCheckedAll={handleCheckedAll}
                  />

                  <AnimationWrapper>
                    {isSuccessModalOpen && (
                      <AlertPopup
                        isShow={isSuccessModalOpen}
                        closeModal={() => {
                          closeSuccessModal();
                        }}
                        customTitle='Are you sure you want to update this permissions?'
                        subTitle={(() => {
                          const data = successCustomValue ?? successModalDataRef.current;
                          const rows = data?.selectedPermissionRows ?? [];
                          if (rows.length === 0) return null;
                          return (
                            <div>
                              <div className='grid grid-cols-12 gap-x-2 gap-y-0 mb-1 text-xs font-semibold text-gray-600 min-w-0'>
                                <div className='col-span-4'>
                                  <Heading variant='h6' title='Permission' />
                                </div>
                                <div className='col-span-1 flex justify-center items-center text-center'>
                                  <Heading variant='h6' title='All' />
                                </div>
                                {(ROLES_PERMISSION_LIST as string[]).map((col) => (
                                  <div
                                    key={col}
                                    className='col-span-1 flex justify-center items-center text-center'>
                                    <Heading variant='h6' title={col} />
                                  </div>
                                ))}
                              </div>
                              <PermissionConfirmScrollWrapper>
                                {rows.map((row, rowIndex) => {
                                  const applicablePerms = row.permissions.filter(
                                    (p) => p.applicable
                                  );
                                  const allAllowed =
                                    applicablePerms.length > 0 &&
                                    applicablePerms.every((p) => p.allowed);
                                  return (
                                    <div
                                      key={`${row.displayName}-${rowIndex}`}
                                      className='grid grid-cols-12 gap-x-2 gap-y-1 items-center py-2 border-b border-gray-200 last:border-b-0 min-w-0'>
                                      <div className='col-span-4 font-semibold text-gray-800 truncate'>
                                        {row.displayName}
                                      </div>
                                      <div className='col-span-1 flex justify-center'>
                                        <span
                                          className='inline-flex h-5 w-5 items-center justify-center rounded border flex-shrink-0'
                                          style={{
                                            backgroundColor: allAllowed ? '#22c55e' : '#f2f2f2',
                                            borderColor: allAllowed ? '#22c55e' : '#d2d2d2',
                                          }}>
                                          {allAllowed && (
                                            <span className='material-symbols-outlined text-white text-sm'>
                                              check
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                      {row.permissions.map((perm, permIndex) => (
                                        <div
                                          key={`${row.displayName}-${perm.text}-${permIndex}`}
                                          className='col-span-1 flex justify-center'>
                                          <span
                                            className='inline-flex h-5 w-5 items-center justify-center rounded border flex-shrink-0'
                                            style={{
                                              backgroundColor: perm.allowed ? '#22c55e' : '#f2f2f2',
                                              borderColor: perm.allowed ? '#22c55e' : '#d2d2d2',
                                            }}>
                                            {perm.allowed && (
                                              <span className='material-symbols-outlined text-white text-sm'>
                                                check
                                              </span>
                                            )}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })}
                              </PermissionConfirmScrollWrapper>
                            </div>
                          );
                        })()}
                        handleSubmit={() => {
                          const data = successCustomValue ?? successModalDataRef.current;
                          upsertPermission(data?.payload ?? data);
                        }}
                        loading={isLoading}
                      />
                    )}
                  </AnimationWrapper>
                </ModalBody>
                <ModalFooterComponent
                  id='manageFeatureSetModalFooter'
                  onCloseModal={closeModal}
                  urlName={getBulkEditUrlName(permissionType)}
                  modalStatus='Add'
                  valueChanged={false}
                  loading={isLoading}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default ManageFeatureSetModal;
